import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// Types
interface User {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  profilePicture: string | null;
}

interface CommentAttachment {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  url: string;
}

interface Comment {
  id: string;
  taskId: string;
  userId: string;
  content: string;
  parentId: string | null;
  isResolved: boolean;
  user?: User;
  attachments?: CommentAttachment[];
  replies?: Comment[];
  createdAt: string;
  updatedAt: string;
}

interface CreateCommentDto {
  content: string;
  parentId?: string;
}

interface UpdateCommentDto {
  content?: string;
  isResolved?: boolean;
}

// Query keys
export const commentKeys = {
  all: ['comments'] as const,
  lists: () => [...commentKeys.all, 'list'] as const,
  byTask: (taskId: string) => [...commentKeys.lists(), 'task', taskId] as const,
  detail: (commentId: string) => [...commentKeys.all, 'detail', commentId] as const,
};

// Fetch comments for a task
export function useCommentsQuery(taskId: string) {
  return useQuery({
    queryKey: commentKeys.byTask(taskId),
    queryFn: async () => {
      const response = await api.get<Comment[]>(`/tasks/${taskId}/comments`);
      return response.data;
    },
    enabled: !!taskId,
    staleTime: 10000, // Shorter stale time for comments
  });
}

// Create comment mutation
export function useCreateComment(taskId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (dto: CreateCommentDto) => {
      const response = await api.post<Comment>(`/tasks/${taskId}/comments`, dto);
      return response.data;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: commentKeys.byTask(taskId),
      });

      const previousComments = queryClient.getQueryData<Comment[]>(
        commentKeys.byTask(taskId)
      );

      // We don't optimistically add since we don't have user info
      // The mutation will refetch after success

      return { previousComments };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousComments) {
        queryClient.setQueryData(
          commentKeys.byTask(taskId),
          context.previousComments
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.byTask(taskId) });
    },
  });
}

// Update comment mutation
export function useUpdateComment(taskId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      commentId,
      updates,
    }: {
      commentId: string;
      updates: UpdateCommentDto;
    }) => {
      const response = await api.patch<Comment>(
        `/comments/${commentId}`,
        updates
      );
      return response.data;
    },
    onMutate: async ({ commentId, updates }) => {
      await queryClient.cancelQueries({
        queryKey: commentKeys.byTask(taskId),
      });

      const previousComments = queryClient.getQueryData<Comment[]>(
        commentKeys.byTask(taskId)
      );

      if (previousComments) {
        const updateCommentRecursive = (comments: Comment[]): Comment[] =>
          comments.map((c) => {
            if (c.id === commentId) {
              return { ...c, ...updates, updatedAt: new Date().toISOString() };
            }
            if (c.replies) {
              return { ...c, replies: updateCommentRecursive(c.replies) };
            }
            return c;
          });

        queryClient.setQueryData<Comment[]>(
          commentKeys.byTask(taskId),
          updateCommentRecursive(previousComments)
        );
      }

      return { previousComments };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousComments) {
        queryClient.setQueryData(
          commentKeys.byTask(taskId),
          context.previousComments
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.byTask(taskId) });
    },
  });
}

// Delete comment mutation
export function useDeleteComment(taskId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (commentId: string) => {
      await api.delete(`/comments/${commentId}`);
      return commentId;
    },
    onMutate: async (commentId) => {
      await queryClient.cancelQueries({
        queryKey: commentKeys.byTask(taskId),
      });

      const previousComments = queryClient.getQueryData<Comment[]>(
        commentKeys.byTask(taskId)
      );

      if (previousComments) {
        const removeCommentRecursive = (comments: Comment[]): Comment[] =>
          comments
            .filter((c) => c.id !== commentId)
            .map((c) => ({
              ...c,
              replies: c.replies ? removeCommentRecursive(c.replies) : undefined,
            }));

        queryClient.setQueryData<Comment[]>(
          commentKeys.byTask(taskId),
          removeCommentRecursive(previousComments)
        );
      }

      return { previousComments };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousComments) {
        queryClient.setQueryData(
          commentKeys.byTask(taskId),
          context.previousComments
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.byTask(taskId) });
    },
  });
}

// Upload attachment mutation
export function useUploadAttachment(taskId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      commentId,
      file,
    }: {
      commentId: string;
      file: File;
    }) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post<CommentAttachment>(
        `/comments/${commentId}/attachments`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: commentKeys.byTask(taskId) });
    },
  });
}

export type { Comment, CommentAttachment, CreateCommentDto, UpdateCommentDto };
